import { Component, inject, OnInit } from '@angular/core';
import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { finalize, Observable, tap } from 'rxjs';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';

// Font Awesome
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';

// Components
import { NavbarComponent } from '../../../template/components/navbar/navbar.component';
import { EntityInfoComponent } from "../../../shared/components/entity-info/entity-info.component";

// Services
import { ContextService } from '../../services/context.service';
import { AuthService } from '../../../auth/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';

// Models
import { AdminContextListElem, ContextListElem } from '../../models/context.model';


@Component({
  selector: 'app-context-list',
  imports: [
    NavbarComponent,
    AsyncPipe,
    DatePipe,
    NgClass,
    FontAwesomeModule,
    NgbDropdownModule,
    NgxPermissionsModule,
    EntityInfoComponent
],
  templateUrl: './context-list.component.html',
  styleUrl: './context-list.component.scss'
})
export class ContextListComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly contextSrv = inject(ContextService);
  private readonly authSrv = inject(AuthService);
  private readonly permissionsSrv = inject(NgxPermissionsService);
  private readonly toastSrv = inject(ToastService);

  searchValue: string = '';
  contexts$!: Observable<ContextListElem[] | AdminContextListElem[]>;
  isLoading: boolean = false;

  faArrowRight = faArrowRight;

  navList: { label: string, routePath: string }[] = [];

  contextColumns = [
    { label: $localize`Id`, key: 'id', type: 'hidden' },
    { label: $localize`Nome`, key: 'name', type: 'string' },
    { label: $localize`Descrizione`, key: 'description', type: 'string' }
  ];

  hasMadPermission: boolean = false;

  statusTranslationMap: Record<string, string> = {
    IDLE: $localize`Inizializzazione in corso...`,
    COMPLETED: $localize`Attivo`,
    FAILED: $localize`Fallito`
  };

  integrationStatusTranslationMap: Record<string, string> = {
    FRONTEND_INTEGRATION_ALLOWED: $localize`Frontend e Backend`,
    BACKEND_INTEGRATION_ONLY: $localize`Solo Backend`,
    DISABLED: $localize`Disabilitata`
  };

  contestLimitReached = false;

  ngOnInit(): void {
    this.isLoading = true;
    this.permissionsSrv.hasPermission('ROLE_MAD').then((hasPermission) => {
      this.hasMadPermission = hasPermission;
      if (hasPermission) {
        this.navList = [{ label: $localize`Lista Contesti`, routePath: '/contexts' }];
        this.contexts$ = this.contextSrv.listContext()
          .pipe(
            tap((list) => { if (list.length >= 50) { this.contestLimitReached = true } }), // Controlla lunghezza lista contesti, se >= 50 blocca la creazione 
            finalize(() => this.isLoading = false)
          );
      } else {
        this.navList = [{ label: $localize`I miei contesti`, routePath: '/contexts' }];
        this.contexts$ = this.contextSrv.listMyContexts(this.authSrv.getUserId()).pipe(
          finalize(() => this.isLoading = false));
      }
    });
  }

  getFilteredContexts(contexts: ContextListElem[] | AdminContextListElem[]) {
    if (!this.searchValue) return contexts;
    const lowerCaseSearchValue = this.searchValue.toLowerCase();
    return contexts.filter(user =>
      user.name.toLowerCase().includes(lowerCaseSearchValue) ||
      user.description.toLowerCase().includes(lowerCaseSearchValue)
    );
  }

  openContextDetail(context: ContextListElem | AdminContextListElem) {
    if (context.status === 'IDLE') {
      this.toastSrv.show({
        type: 'warning',
        message: 'Contesto non disponibile. Inizializzazione in corso...'
      });
      return;
    }

    const route = this.hasMadPermission
      ? ['/contexts', context.id]
      : [`/contexts/${context.id}/management`];

    this.router.navigate(route, { state: { context } });
  }

  navigateGroupDetail(groupId: string) {
    if (this.hasMadPermission) {
      this.router.navigate(['/groups', groupId]);
    }
  }

  isContextListElem(obj: ContextListElem | AdminContextListElem): obj is ContextListElem {
    return 'groups' in obj;
  }
}
